import React, { useEffect, useState } from "react";
import { X, Warning, TrendDown, TrendUp, Fire, CalendarX, ArrowsClockwise, Stack, Target, CalendarCheck, Scales, Timer } from "phosphor-react";

/**
 * AlertPopup - A non-intrusive popup for trading alerts
 * Props:
 *   - alerts: Array of { id, type, title, message }
 *   - onDismiss: (alertId) => void - Called when user dismisses an alert
 */
export default function AlertPopup({ alerts = [], onDismiss }) {
  const [visibleAlerts, setVisibleAlerts] = useState([]);

  useEffect(() => {
    setVisibleAlerts(alerts);
  }, [alerts]);

  if (visibleAlerts.length === 0) return null;

  const getAlertIcon = (type, severity) => {
    switch (type) {
      case 'losing_streak':
        return <TrendDown size={20} weight="fill" className="text-[#FF3B30]" />;
      case 'daily_drawdown':
        return <Warning size={20} weight="fill" className="text-[#FF9500]" />;
      case 'weekly_drawdown':
        return <CalendarX size={20} weight="fill" className="text-[#FF6B00]" />;
      case 'overtrading':
        return <Fire size={20} weight="fill" className="text-[#FF3B30]" />;
      case 'revenge_trade':
        return <ArrowsClockwise size={20} weight="fill" className="text-[#AF52DE]" />;
      case 'winning_streak':
        return <TrendUp size={20} weight="fill" className="text-[#00E676]" />;
      case 'max_open_positions':
        return <Stack size={20} weight="fill" className="text-[#FF9500]" />;
      case 'daily_target':
        return <Target size={20} weight="fill" className="text-[#30D158]" />;
      case 'weekly_target':
        return <CalendarCheck size={20} weight="fill" className="text-[#30D158]" />;
      case 'directional_exposure':
        return <Scales size={20} weight="fill" className="text-[#FF9500]" />;
      case 'hold_time_drift':
        // Strong alert (red) vs Soft alert (yellow)
        return severity === 'strong' 
          ? <Timer size={20} weight="fill" className="text-[#FF453A]" />
          : <Timer size={20} weight="fill" className="text-[#FFD60A]" />;
      default:
        return <Warning size={20} weight="fill" className="text-[#FF9500]" />;
    }
  };

  const getAlertColor = (type, severity) => {
    switch (type) {
      case 'losing_streak':
        return 'border-[#FF3B30]/30 bg-[#FF3B30]/5';
      case 'daily_drawdown':
        return 'border-[#FF9500]/30 bg-[#FF9500]/5';
      case 'weekly_drawdown':
        return 'border-[#FF6B00]/30 bg-[#FF6B00]/5';
      case 'overtrading':
        return 'border-[#FF3B30]/30 bg-[#FF3B30]/5';
      case 'revenge_trade':
        return 'border-[#AF52DE]/30 bg-[#AF52DE]/5';
      case 'winning_streak':
        return 'border-[#00E676]/30 bg-[#00E676]/5';
      case 'max_open_positions':
        return 'border-[#FF9500]/30 bg-[#FF9500]/5';
      case 'daily_target':
        return 'border-[#30D158]/30 bg-[#30D158]/5';
      case 'weekly_target':
        return 'border-[#30D158]/30 bg-[#30D158]/5';
      case 'directional_exposure':
        return 'border-[#FF9500]/30 bg-[#FF9500]/5';
      case 'hold_time_drift':
        // Strong alert (red) vs Soft alert (yellow)
        return severity === 'strong'
          ? 'border-[#FF453A]/30 bg-[#FF453A]/5'
          : 'border-[#FFD60A]/30 bg-[#FFD60A]/5';
      default:
        return 'border-white/20 bg-white/5';
    }
  };

  const handleDismiss = (alertId) => {
    setVisibleAlerts(prev => prev.filter(a => a.id !== alertId));
    if (onDismiss) {
      onDismiss(alertId);
    }
  };

  return (
    <div 
      className="fixed bottom-6 right-6 z-50 flex flex-col gap-3 max-w-sm"
      data-testid="alert-popup-container"
    >
      {visibleAlerts.map((alert) => (
        <div
          key={alert.id}
          className={`relative p-4 rounded-lg border backdrop-blur-sm shadow-xl animate-slide-in-right ${getAlertColor(alert.type, alert.severity)}`}
          data-testid={`alert-popup-${alert.type}`}
        >
          <button
            onClick={() => handleDismiss(alert.id)}
            className="absolute top-2 right-2 p-1 text-white/40 hover:text-white transition-colors"
            data-testid={`alert-dismiss-${alert.id}`}
          >
            <X size={16} weight="bold" />
          </button>
          
          <div className="flex items-start gap-3 pr-6">
            <div className="mt-0.5 shrink-0">
              {getAlertIcon(alert.type, alert.severity)}
            </div>
            <div>
              <h4 className="font-semibold text-white text-sm mb-1">
                {alert.title}
              </h4>
              <p className="text-white/60 text-xs leading-relaxed">
                {alert.message}
              </p>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
